import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {
    exchangeGoogleCodeForIDToken,
    getErrorMessageByCode,
    GoogleButton,
    GoogleIcon,
    initCodeClient,
    type CodeResponse,
} from "@khan/auth-google";
import type {LoginType} from "@khan/auth-shared";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {doNotTranslate} from "@khan/i18n";
import {type Location} from "@khan/urls";

// eslint-disable-next-line static-service/require-shared-package-imports-only -- TODO: Port this over to the auth package.
import {renderInfoToast} from "~/notifications-package/toast-notification.tsx";

import RequireReauth from "../require-reauth.tsx";

interface Props {
    appleId: string | null | undefined;
    facebookId: string | null | undefined;
    googlePrimaryEmail: string | null | undefined;
    gqlClient: ApolloGqlClient;
    identifier: string | null | undefined;
    isClasslinkUser: boolean;
    linkAccountWith: (loginType: LoginType, token: string) => void;
    location: Location;
    onDisconnect: () => void;
    renderThirdPartyDisconnectButton: (provider: LoginType) => React.ReactNode;
    setWarningMessage: (message: string | null) => void;
}

export const LinkGoogleAccount: React.FC<Props> = ({
    gqlClient,
    googlePrimaryEmail,
    identifier,
    isClasslinkUser,
    setWarningMessage,
    facebookId,
    appleId,
    location,
    linkAccountWith,
    renderThirdPartyDisconnectButton,
}) => {
    const [showGoogleLoginButton, setShowGoogleLoginButton] =
        React.useState(false);

    /**
     * Notifies to Sentry in case we get any error from the Google auth API or
     * from the GraphQL mutation.
     * @param {Error} error The error thrown in the current context.
     */
    const handleGoogleLoginError = (error: Error) => {
        const errorMessage = getErrorMessageByCode(error.message);
        renderInfoToast(errorMessage);
        setShowGoogleLoginButton(true);
    };

    /**
     * Handle the response from Google's OAuth2 code flow.
     *
     * This is called when the user has been authenticated by Google and we have
     * a code to exchange for an ID token.
     */
    const handleCodeResponse = (response: CodeResponse) => {
        const code = response.code;
        exchangeGoogleCodeForIDToken(gqlClient, code, location.origin).then(
            (idToken) => {
                linkAccountWith("google", idToken);
            },
        );
    };

    /**
     * Try to add a Google account to the current user.
     *
     * This uses the Google Identity Services flow (GSI) to add a Google account
     * to the current user.
     */
    const authenticateWithGoogleIdentityServices = () => {
        initCodeClient(handleCodeResponse, setWarningMessage)
            .then((client) => {
                client.requestCode();
            })
            .catch((error) => {
                // Auth error
                if (error.error) {
                    handleGoogleLoginError(new Error(error.error));
                }
            });
    };

    const renderGoogleLoginButton = () => {
        if (showGoogleLoginButton) {
            return (
                <GoogleButton
                    reauth={true}
                    size="small"
                    continueUrl={location.pathname}
                    style={{minInlineSize: 210}}
                />
            );
        }

        return (
            <RequireReauth
                identifier={identifier}
                showClasslinkAuthProvider={isClasslinkUser}
                /**
                 * Try to add Google account to the current user using the new login flow.
                 */
                onSuccess={authenticateWithGoogleIdentityServices}
                onFailure={() => {}}
                hasGoogleButton={false}
                // We check the other providers if the user has an account registered with them
                hasFacebookButton={!!facebookId}
                hasAppleButton={!!appleId}
            >
                {(invokeReauth) => (
                    <Button
                        onClick={() => {
                            setWarningMessage(null);
                            invokeReauth();
                        }}
                        size="small"
                    >
                        {t`Connect Google`}
                    </Button>
                )}
            </RequireReauth>
        );
    };

    const googleEmailWithDisconnect = (
        <View style={styles.row}>
            {/* Not all accounts with google ids have logged in with google since
            we started tracking goog's primary email. We simply say "Connected"
            in this case. */}
            <LabelMedium style={[styles.verticalCenter, styles.label]}>
                {googlePrimaryEmail || t`Connected`}
            </LabelMedium>
            <Strut size={spacing.small_12} />
            {renderThirdPartyDisconnectButton("google")}
        </View>
    );

    return (
        <View style={[styles.row, styles.thirdPartyButtonContainer]}>
            <View
                style={[
                    styles.row,
                    styles.thirdPartyButtonContainer,
                    styles.alignLabels,
                ]}
            >
                <View style={[styles.verticalCenter, styles.thirdPartyIcon]}>
                    <GoogleIcon ariaHidden={true} />
                </View>
                <LabelMedium style={[styles.verticalCenter, styles.label]}>
                    {doNotTranslate`Google`}
                </LabelMedium>
            </View>
            <Strut size={spacing.medium_16} />
            {googlePrimaryEmail
                ? googleEmailWithDisconnect
                : renderGoogleLoginButton()}
        </View>
    );
};

const styles = StyleSheet.create({
    label: {
        color: color.offBlack,
    },
    thirdPartyIcon: {
        margin: spacing.xSmall_8,
        width: spacing.medium_16,
        height: spacing.medium_16,
    },
    row: {
        alignItems: "center",
        flexDirection: "row",
    },
    verticalCenter: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    thirdPartyButtonContainer: {
        height: spacing.xLarge_32,
        display: "flex",
        alignItems: "center",
    },
    alignLabels: {
        flex: "0 0 100px",
    },
});
export default LinkGoogleAccount;
